import { default as React, useState } from "react"
import { OverlayTrigger, Tooltip } from "react-bootstrap"
import { FormattedMessage, defineMessages, injectIntl } from "react-intl"
import { ConnectedProps, connect } from "react-redux"
import Icon from "../../common/icon/icon.component"
import ItemBulkUpdateAssignedUser from "../../common/quote/component/ItemBulkUpdateAssignedUser"
import QuoteItemDiscount from "../../common/quote/itemdiscount.component.jsx"
import QuoteItemSettings from "../../common/quote/itemsettings.component.jsx"
import { STOCK_DOCUMENT_STATUS } from "../../common/types/inventory"
import { GlobalHelper } from "../../indexImports"
import { DOCUMENT_TYPE } from "../../state/Constants"
import IntlProps from "../../state/helpers/IntlProps"
import { StockDocumentAttributes } from "../../state/inventory/stockdocument/models"
import { ORDER } from "../../state/order/Constants"
import { AdviceDto } from "../../state/pricing/Advice"
import { PricingFeeCalculationDto } from "../../state/pricing/PricingFeeCalculation"
import { patchPricingTableItem } from "../../state/pricing/actions"
import { getPricingTableItem } from "../../state/pricing/selectors"
import { UserDto } from "../../state/user/User"
import { AppContext } from "@lightspeed-invoicing/core-client/frontend"

type OwnProps = {
    itemId: string
    mainObjectId: string
    type: DOCUMENT_TYPE
    advice?: AdviceDto
    fees: PricingFeeCalculationDto[]
    isDiscountDisabled?:boolean
    users: UserDto[]
    assignedToUserId: string
    shouldEditQuantity:boolean
    allowEdit: boolean
    fromShowDetails: boolean
    getTableItemTax: () => object | undefined
    onEditLinkClick: () => void
    onUpdateCallback: () => void
    onChange: (id: string) => void
    stockDocument: StockDocumentAttributes | undefined
}

const messages = defineMessages({
    "settings_label": {
        id: "pricing.components.setting",
        defaultMessage: "Setting",
    },
    "discount_label": {
        id: "quote.edit.price_panel_component.discount_label",
        defaultMessage: "Discount",
    },
})


const SettingPanelComponent = (props: Props  & IntlProps) => {
    const isInventoryEnabled = AppContext.isInventoryEnabled()
    const onEditLinkClickCallback = props.onEditLinkClick
    const [assignedUserModal, setAssignedUserModal] = useState(<></>)
    const [, setAssignedUserId] = useState(props.item?.attributes.assignedToUserId)


    const closeSetAssignedUserModal = (assignedToUserId: string | undefined) => {
        if (assignedToUserId) {
            setAssignedUserId(assignedToUserId)
            props.onChange(assignedToUserId)
        }
        setAssignedUserModal(<></>)
    }

    const getAssignedUserName = () => {
        const assignedTo = props.item ? props.item.attributes.assignedToUserId : ""
        const user = props.users.find(u => u.id == assignedTo)
        return user
    }

    const openSetAssignedUserModal = (event: React.MouseEvent) => {
        setAssignedUserModal(<ItemBulkUpdateAssignedUser
            assignedToUserId={props.item ? props.item.attributes.assignedToUserId : ""}
            onUpdateCallback={() => props.onUpdateCallback()}
            objectId={props.mainObjectId}
            mainObject={props.type}
            itemIdList={new Set([props.item?.id ?? ""])}
            onClose={(assignedToUserId) => closeSetAssignedUserModal(assignedToUserId)}
        />)
        event.preventDefault()
    }

    const discountEnabled=()=>{
        const hasDiscount=props.item?.attributes.discountValue!=0
        const unitPrice = !props.item?.attributes.isTaxInclusive ? props.item?.attributes.unitPrice : 
            props.item?.attributes.unitPriceInclusive
        return ((props.item && Number(unitPrice)==Number(props.item.attributes.calculatedUnitPrice) )|| 
        hasDiscount) && !props.isDiscountDisabled
    }
    
    const assignedUser = getAssignedUserName()
    const showAssignedUser = props.users 
        && props.allowEdit 
        && props.item 
        && assignedUser 
        && props.type == ORDER 
        || isInventoryEnabled && props.stockDocument

    const assignedUserDisabled = isInventoryEnabled 
        && props.stockDocument 
        && props.stockDocument.status.attributes.state != STOCK_DOCUMENT_STATUS.DRAFT
    
    return (
        <div id={props.allowEdit ? "menuButtons" : "menuButtonsModal"} className="d-flex px-2 mt-1">
            {showAssignedUser &&
                <OverlayTrigger
                    placement={"top"}
                    trigger={["hover","focus"]}
                    overlay={<Tooltip id="tooltip_assigned_user">
                        <FormattedMessage
                            id="price.list.pricing.assigned._to_user"
                            defaultMessage="Assigned to {userNameAssigned}"
                            values={{
                                userNameAssigned: GlobalHelper.GetFormattedName(assignedUser)
                            }} />
                    </Tooltip>}
                >
                    <button type="button" className="d-inline-flex btn btn-sm btn-outline-primary py-1"
                        style={{ fontSize: "0.6rem", maxWidth: "50px", overflow: "hidden", height: "32px", alignItems: "center" }}
                        onClick={openSetAssignedUserModal} disabled={assignedUserDisabled}>
                        <Icon value="user"/>
                        {GlobalHelper.GetFormattedNameInitials(assignedUser)}
                    </button>
                </OverlayTrigger>
            }

            {props.item && props.allowEdit && props.shouldEditQuantity &&
                <QuoteItemSettings
                    title={`${props.intl.formatMessage(messages["settings_label"])} ${props.item?.attributes.activeTitle}`}
                    fees={props.fees}
                    type={props.type}
                    className={"ml-2"}
                    editable={props.allowEdit}
                    shouldEditQuantity={props.shouldEditQuantity}
                    itemId={props.itemId}
                    mainObjectId={props.mainObjectId}
                    onChange={() => props.onUpdateCallback()}
                    tableTax={props.getTableItemTax()}
                    onEditLinkClick={onEditLinkClickCallback}
                />
            }
            <div className={props.allowEdit && discountEnabled() ? "" : "pl-2"}>
                <QuoteItemDiscount
                    isPricingTable={true}
                    title={props.intl.formatMessage(messages["discount_label"]) + props.item?.attributes.activeTitle}
                    type={props.type}
                    dropdownStyle={{ top: "auto", left: "auto" }}
                    currency={props.item?.attributes.currency}
                    editable={props.allowEdit && discountEnabled()}
                    itemId={props.itemId}
                    mainObjectId={props.mainObjectId} />
            </div>
            {assignedUserModal}
        </div>
    )
}

const mapState = (state, props: OwnProps) => {
    const id = props.itemId
    const objectId = props.mainObjectId
    const item = getPricingTableItem(state, id, objectId)
    return {
        ...props,
        item: item,
    }
}
const mapDispatch = { patchPricingTableItem }
const connector = connect(mapState, mapDispatch)
type Props = ConnectedProps<typeof connector>
export default connector(injectIntl(SettingPanelComponent))


// WEBPACK FOOTER //
// ./src/pricing/components/SettingPanelComponent.tsx